
import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger 
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export type Notification = {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  type: "warning" | "alert" | "info";
  itemId?: string;
};

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  
  // Only count unread notifications
  const unreadCount = notifications.filter(n => !n.isRead).length;
  
  // Fetch notifications when user is logged in
  useEffect(() => {
    if (!user) return;
    
    // First check local storage for read notifications
    const getReadNotificationIds = () => {
      const storedIds = localStorage.getItem(`read_notifications_${user.id}`);
      return storedIds ? JSON.parse(storedIds) : [];
    };

    const readNotificationIds = getReadNotificationIds();
    
    const fetchNotifications = async () => {
      try {
        // Fetch expiring items from inventory
        const { data: expiringItems, error: expiringError } = await supabase
          .from('inventory_items')
          .select('id, name, expiry_date')
          .eq('user_id', user.id)
          .is('has_expiry', true)
          .not('expiry_date', 'is', null);
        
        if (expiringError) throw expiringError;
        
        const today = new Date();
        const threeDaysFromNow = new Date();
        threeDaysFromNow.setDate(today.getDate() + 3);
        
        let newNotifications: Notification[] = [];
        
        // Generate notifications for expired items
        const expiredItems = expiringItems?.filter(item => {
          const expiryDate = new Date(item.expiry_date);
          return expiryDate < today;
        }) || [];
        
        expiredItems.forEach(item => {
          const notificationId = `expired-${item.id}`;
          // Only add if it's not already in the read notifications
          if (!readNotificationIds.includes(notificationId)) {
            newNotifications.push({
              id: notificationId,
              title: "Item Expired",
              message: `${item.name} has expired`,
              isRead: false,
              createdAt: new Date().toISOString(),
              type: "alert",
              itemId: item.id
            });
          }
        });
        
        // Generate notifications for items expiring soon
        const expiringItemsSoon = expiringItems?.filter(item => {
          const expiryDate = new Date(item.expiry_date);
          return expiryDate >= today && expiryDate <= threeDaysFromNow;
        }) || [];
        
        expiringItemsSoon.forEach(item => {
          const expiryDate = new Date(item.expiry_date);
          const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          const notificationId = `expiring-${item.id}`;
          
          // Only add if it's not already in the read notifications
          if (!readNotificationIds.includes(notificationId)) {
            newNotifications.push({
              id: notificationId,
              title: "Expiring Soon",
              message: `${item.name} expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`,
              isRead: false,
              createdAt: new Date().toISOString(),
              type: "warning",
              itemId: item.id
            });
          }
        });
        
        // Only show "All Good" notification if there are no unread notifications and no existing notifications
        if (newNotifications.length === 0 && readNotificationIds.length === 0) {
          const allGoodId = 'all-good-' + Date.now();
          newNotifications.push({
            id: allGoodId,
            title: "All Good",
            message: "No items are expiring soon",
            isRead: false,
            createdAt: new Date().toISOString(),
            type: "info"
          });
        }
        
        setNotifications(newNotifications);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    
    fetchNotifications();
    
    // Set up realtime subscription to inventory changes
    const channel = supabase
      .channel('inventory_notifications')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'inventory_items',
          filter: user ? `user_id=eq.${user.id}` : undefined
        }, 
        (payload) => {
          console.log('Inventory change received:', payload);
          
          // Get current read notification IDs
          const currentReadIds = getReadNotificationIds();
          
          // Handle different types of changes
          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as any;
            const expiryDate = newItem.expiry_date ? new Date(newItem.expiry_date) : null;
            const today = new Date();
            
            if (expiryDate) {
              // Calculate days until expiry
              const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              
              // Create appropriate notification
              if (daysUntilExpiry < 0) {
                // Item is already expired
                const notificationId = `expired-${newItem.id}`;
                // Check if notification is already read
                if (!currentReadIds.includes(notificationId)) {
                  const notification: Notification = {
                    id: notificationId,
                    title: "Item Expired",
                    message: `${newItem.name} has expired`,
                    isRead: false,
                    createdAt: new Date().toISOString(),
                    type: "alert",
                    itemId: newItem.id
                  };
                  
                  setNotifications(prev => [notification, ...prev]);
                  toast.warning(`${newItem.name} has expired`, {
                    position: "bottom-right",
                    dismissible: true,
                  });
                }
              } 
              else if (daysUntilExpiry <= 3) {
                // Item expires soon (within 3 days)
                const notificationId = `expiring-${newItem.id}`;
                // Check if notification is already read
                if (!currentReadIds.includes(notificationId)) {
                  const notification: Notification = {
                    id: notificationId,
                    title: "Expiring Soon",
                    message: `${newItem.name} expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`,
                    isRead: false,
                    createdAt: new Date().toISOString(),
                    type: "warning",
                    itemId: newItem.id
                  };
                  
                  setNotifications(prev => [notification, ...prev]);
                  toast.warning(`${newItem.name} expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`, {
                    position: "bottom-right",
                    dismissible: true,
                  });
                }
              }
            }
          } 
          else if (payload.eventType === 'UPDATE') {
            // Handle updates if needed - could check if expiry date changed
            const updatedItem = payload.new as any;
            const oldItem = payload.old as any;
            
            // Only create notification if expiry date was updated
            if (updatedItem.expiry_date !== oldItem.expiry_date) {
              const notificationId = `updated-${updatedItem.id}-${Date.now()}`;
              // Check if notification is already read
              if (!currentReadIds.includes(notificationId)) {
                const notification: Notification = {
                  id: notificationId,
                  title: "Item Updated",
                  message: `Expiry date for ${updatedItem.name} has been updated`,
                  isRead: false,
                  createdAt: new Date().toISOString(),
                  type: "info",
                  itemId: updatedItem.id
                };
                
                setNotifications(prev => [notification, ...prev]);
              }
            }
          }
          else if (payload.eventType === 'DELETE') {
            // Remove any notifications for the deleted item
            const deletedItemId = payload.old.id;
            setNotifications(prev => 
              prev.filter(notif => notif.itemId !== deletedItemId)
            );
          }
        }
      )
      .subscribe();
      
    // Set a 1 hour interval to refresh notifications (for expiry dates changing status)
    const refreshInterval = setInterval(fetchNotifications, 60 * 60 * 1000);
      
    return () => {
      supabase.removeChannel(channel);
      clearInterval(refreshInterval);
    };
  }, [user]);
  
  // Update localStorage when a notification is marked as read
  const saveReadNotifications = (readIds: string[]) => {
    if (!user) return;
    localStorage.setItem(`read_notifications_${user.id}`, JSON.stringify(readIds));
  };

  // Mark a single notification as read and remove it from the list
  const markAsRead = (id: string) => {
    setNotifications(prev => {
      // Filter out the notification that was marked as read
      const remainingNotifications = prev.filter(notification => 
        notification.id !== id
      );
      
      // Get all read notification IDs including the one just read
      const readIds = [...prev.filter(notification => notification.isRead).map(n => n.id), id];
      
      // Save to localStorage
      saveReadNotifications(readIds);
      
      return remainingNotifications;
    });
  };
  
  // Mark all notifications as read and clear the list
  const markAllAsRead = () => {
    const allIds = notifications.map(notification => notification.id);
    saveReadNotifications(allIds);
    setNotifications([]);
  };
  
  const getTypeColor = (type: Notification["type"]) => {
    switch (type) {
      case "alert": return "bg-red-500 text-white";
      case "warning": return "bg-amber-500 text-white";
      case "info": return "bg-expitrack-primary text-white";
      default: return "bg-gray-200 text-gray-800";
    }
  };
  
  if (!user) return null;
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Notifications">
          <Bell className="w-5 h-5 text-gray-600" />
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
              >
                {unreadCount}
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-50" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-medium">Notifications</h3>
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-xs text-expitrack-primary hover:underline"
            >
              Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map(notification => (
              <div 
                key={notification.id}
                className="p-4 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => markAsRead(notification.id)}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notification.type === 'alert' ? 'bg-red-500' : notification.type === 'warning' ? 'bg-amber-500' : 'bg-expitrack-primary'}`}></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm">{notification.title}</span>
                      <Badge variant="outline" className={`text-xs ${getTypeColor(notification.type)}`}>
                        {notification.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(notification.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              No notifications yet
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
